import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { zoomCard } from "@/components/CardZoom";
import { Package, Loader2, Coins, CreditCard, X, Sparkles } from "lucide-react";

export const CW_TIERS = [
  { key: "icon", label: "Icon", cls: "text-pb-red", chip: "border-pb-red/70 text-pb-red" },
  { key: "legendary", label: "Legendary", cls: "text-amber-300", chip: "border-amber-400/70 text-amber-300" },
  { key: "elite", label: "Elite", cls: "text-yellow-400", chip: "border-yellow-500/70 text-yellow-400" },
  { key: "rare", label: "Rare", cls: "text-sky-400", chip: "border-sky-500/60 text-sky-400" },
  { key: "common", label: "Common", cls: "text-zinc-400", chip: "border-zinc-600 text-zinc-400" },
];
const TIER_META = Object.fromEntries(CW_TIERS.map((t) => [t.key, t]));

function PackReveal({ reveal, onClose }) {
  return (
    <div className="fixed inset-0 z-[95] bg-black/95 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="cw-pack-reveal" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-pb-surface border border-yellow-400/60 p-6 animate-fade-up">
        <div className="flex items-center justify-between mb-1">
          <div className="font-display font-black uppercase text-xl text-yellow-400 flex items-center gap-2">
            <Sparkles className="w-5 h-5" /> {reveal.pack} — Ripped!
          </div>
          <button data-testid="cw-pack-reveal-close" onClick={onClose}><X className="w-5 h-5 text-zinc-500 hover:text-white" /></button>
        </div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">{reveal.pulls.length} cards added to your vault</p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {reveal.pulls.map((p) => {
            const t = TIER_META[p.tier] || TIER_META.common;
            return (
              <div key={p.id} className={`border p-1.5 bg-black ${p.one_of_one ? "border-amber-400/80" : "border-pb-border"}`} data-testid={`cw-pull-${p.id}`}>
                <img src={p.url} alt={p.name} className="w-full aspect-[3/4] object-contain bg-black cursor-zoom-in"
                  onClick={() => zoomCard(p.url, p.name)} />
                <div className="text-[9px] font-semibold truncate mt-1">{p.name}</div>
                <div className={`text-[7px] font-black uppercase tracking-widest ${t.cls}`}>
                  {t.label}{p.one_of_one ? " · 1/1" : p.edition_num ? ` · #${p.edition_num}/${p.edition_total}` : ""}
                </div>
              </div>
            );
          })}
        </div>
        <button data-testid="cw-pack-reveal-done" onClick={onClose}
          className="mt-5 w-full bg-yellow-400 text-black font-black uppercase tracking-widest py-3 hover:bg-[#BEF264] transition-colors">
          To Battle
        </button>
      </div>
    </div>
  );
}

const PackBox = ({ p, busy, onBlc, onUsd }) => (
  <div data-testid={`cw-pack-${p.id}`}
    className={`relative flex flex-col border p-4 bg-gradient-to-b ${p.limited ? "border-pb-red/60 from-red-950/30 to-pb-surface" : "border-yellow-400/30 from-pb-elevated/40 to-pb-surface"}`}>
    {p.limited && (
      <span className="absolute -top-2 right-3 text-[8px] font-black uppercase tracking-[0.25em] text-white bg-pb-red px-2 py-0.5 animate-pulse">Limited</span>
    )}
    <div className="flex items-center gap-2 mb-0.5">
      <Package className={`w-4 h-4 ${p.limited ? "text-pb-red" : "text-yellow-400"}`} />
      <span className="font-display font-black uppercase text-sm leading-tight">{p.name}</span>
    </div>
    <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-3">{p.cards} cards per pack</div>
    <div className="space-y-1 mb-3">
      {CW_TIERS.map((t) => {
        const v = Number(p.odds?.[t.key] || 0);
        if (v <= 0) return null;
        return (
          <div key={t.key} className="flex items-center justify-between border-b border-dotted border-zinc-800 pb-0.5">
            <span className={`text-[9px] font-black uppercase tracking-widest ${t.cls}`}>{t.label}</span>
            <span className="text-[10px] font-mono text-zinc-300">{v}%</span>
          </div>
        );
      })}
    </div>
    <div className="text-[9px] uppercase tracking-widest text-yellow-400/90 mb-4" data-testid={`cw-pack-${p.id}-guarantee`}>
      ✔ Guaranteed: {p.guarantee}
    </div>
    <div className="mt-auto grid grid-cols-2 gap-2">
      <button data-testid={`cw-pack-${p.id}-usd-btn`} onClick={() => onUsd(p)} disabled={busy || !(p.usd > 0)}
        className="flex items-center justify-center gap-1.5 bg-white text-black text-[11px] font-black uppercase tracking-widest py-2.5 hover:bg-zinc-200 active:scale-[0.97] transition-all disabled:opacity-40">
        <CreditCard className="w-3.5 h-3.5" /> ${Number(p.usd).toFixed(0)}
      </button>
      <button data-testid={`cw-pack-${p.id}-blc-btn`} onClick={() => onBlc(p)} disabled={busy || !(p.blc > 0)}
        className="flex items-center justify-center gap-1.5 bg-yellow-400 text-black text-[11px] font-black uppercase tracking-widest py-2.5 hover:bg-[#BEF264] active:scale-[0.97] transition-all disabled:opacity-40">
        <Coins className="w-3.5 h-3.5" /> {Number(p.blc)} BLC
      </button>
    </div>
  </div>
);

export default function CardWarsPacks() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null);
  const pollRef = useRef(null);
  useEffect(() => {
    api.get("/cardwars/packs").then(({ data }) => setData(data)).catch(() => {});
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) {
      window.history.replaceState({}, "", "/card-wars");
      let tries = 0;
      toast.info("Confirming your pack purchase…");
      pollRef.current = setInterval(async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/cardwars/packs/session/${sid}`);
          if (data.payment_status === "paid") {
            clearInterval(pollRef.current);
            setReveal({ pack: data.pack, pulls: data.pulls });
          } else if (["expired", "failed"].includes(data.payment_status) || tries > 20) {
            clearInterval(pollRef.current);
            if (tries > 20) toast.error("Still waiting on payment confirmation — your cards will land automatically once it clears.");
          }
        } catch { clearInterval(pollRef.current); }
      }, 2500);
    }
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);
  if (!data || !data.packs?.length) return null;

  const ripBlc = async (p) => {
    if (!window.confirm(`Rip a ${p.name} for ${p.blc} BLC? ${p.cards} cards, guaranteed: ${p.guarantee}.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cardwars/packs/${p.id}/rip`);
      setReveal(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const payUsd = async (p) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cardwars/packs/${p.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="mb-8 animate-fade-up" data-testid="cw-packs-section">
      <div className="flex flex-wrap items-baseline gap-3 mb-3">
        <span className="font-display font-black uppercase text-lg flex items-center gap-2">
          <Package className="w-5 h-5 text-yellow-400" /> Fill Your Vault
        </span>
        <span className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">
          Rip packs to build your battle deck · pay by card or BLC · {data.pool_size} cards in the pool
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {data.packs.map((p) => <PackBox key={p.id} p={p} busy={busy} onBlc={ripBlc} onUsd={payUsd} />)}
      </div>
      {reveal && <PackReveal reveal={reveal} onClose={() => { setReveal(null); window.dispatchEvent(new Event("cw-refresh")); }} />}
    </div>
  );
}
